/**
 * DOB limits by gender:
 * Male   → at least 21 years old
 * Female → at least 18 years old
 * Others → at least 18 years old
 */

export function getMinAgeForGender(gender) {
  const g = String(gender || '').trim().toLowerCase();
  if (g === 'male') return 21;
  if (g === 'female') return 18;
  return 18;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** YYYY-MM-DD for "today minus N years" (exact calendar day). */
export function getMaxDobDate(minAgeYears) {
  const today = new Date();
  const y = today.getFullYear() - minAgeYears;
  const m = today.getMonth() + 1;
  const d = today.getDate();
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

/** Oldest allowed DOB (default 70 years). */
export function getMinDobDate(maxAgeYears = 70) {
  const today = new Date();
  const y = today.getFullYear() - maxAgeYears;
  const m = today.getMonth() + 1;
  const d = today.getDate();
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

export function getDobLimitsForGender(gender) {
  const minAge = getMinAgeForGender(gender);
  return {
    minAge,
    maxDOB: getMaxDobDate(minAge),
    minDOB: getMinDobDate(70),
  };
}

export function calculateAge(birthdate) {
  if (!birthdate) return 0;

  if (typeof birthdate === 'string') {
    const trimmed = birthdate.trim();
    if (!trimmed || trimmed.startsWith('0000')) return 0;
    const parts = trimmed.split(/[-/]/).map(p => parseInt(p, 10));
    if (parts.length === 3 && !parts.some(isNaN) && parts[0] > 1000) {
      const [birthYear, birthMonth, birthDay] = parts;
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1; // 1-12
      const currentDay = today.getDate();

      let age = currentYear - birthYear;
      if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
        age--;
      }
      return Math.max(0, age);
    }
  }

  const birth = new Date(birthdate);
  if (Number.isNaN(birth.getTime())) return 0;

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return Math.max(0, age);
}

export function isDobValidForGender(dob, gender) {
  if (!dob || typeof dob !== 'string') return false;
  const parts = dob.trim().split(/[-/]/).map(p => parseInt(p, 10));
  if (parts.length !== 3 || parts.some(isNaN)) return false;
  const [y, m, d] = parts;
  if (y < 1900 || m < 1 || m > 12 || d < 1 || d > 31) return false;

  const dateObj = new Date(y, m - 1, d);
  if (dateObj.getFullYear() !== y || dateObj.getMonth() !== m - 1 || dateObj.getDate() !== d) {
    return false;
  }

  const minAge = getMinAgeForGender(gender);
  const age = calculateAge(dob);
  return age >= minAge && age <= 100;
}

